import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Button, message, Modal, Trans } from '@/components'
import {
  getStorageBillingMode,
  markBillingSwitchCompleted,
  StorageBillingMode,
} from '@/utils/storage-billing'

const SNAPSHOT_RETRY_DELAYS_MS = [2000, 4000, 8000]
const VOLUME_REMOVAL_POLL_INTERVAL_MS = 5000
const VOLUME_REMOVAL_POLL_TIMEOUT_MS = 60000

type FailedStep = { resource: 'image' | 'snapshot'; code: number } | null

interface SwitchBillingModalProps {
  open: boolean
  imageOnSpaceBilling: boolean
  snapshotOnSpaceBilling: boolean
  scope?: 'all' | 'snapshotOnly'
  onClose: () => void
  onOk: () => void
}

export function SwitchBillingModal({
  open,
  imageOnSpaceBilling,
  snapshotOnSpaceBilling,
  scope = 'all',
  onClose,
  onOk,
}: SwitchBillingModalProps) {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [partial, setPartial] = useState(false)
  const [failedStep, setFailedStep] = useState<FailedStep>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (open) {
      setSubmitting(false)
      setPartial(false)
      setFailedStep(null)
    }
  }, [open])

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const fetchMode = async (): Promise<StorageBillingMode> => {
    const res = await API.getVolumes()
    return getStorageBillingMode(res?.volumes)
  }

  const switchSnapshotWithRetry = async (): Promise<{ success: boolean; code: number }> => {
    let lastCode = -1
    for (let attempt = 0; attempt <= SNAPSHOT_RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) {
        await sleep(SNAPSHOT_RETRY_DELAYS_MS[attempt - 1])
        if (!mountedRef.current) return { success: false, code: lastCode }
        const mode = await fetchMode().catch(() => null)
        if (mode && !mode.snapshotOnSpaceBilling) return { success: true, code: 0 }
      }
      const res: any = await API.switchSnapshotBilling(
        { switch_to_independent_billing: true },
        { errMsg: false },
      ).catch(() => ({ code: -1 }))
      if (res?.code === 0) return { success: true, code: 0 }
      lastCode = typeof res?.code === 'number' ? res.code : -1
    }
    return { success: false, code: lastCode }
  }

  const waitVolumesRemoved = async () => {
    const startedAt = Date.now()
    for (;;) {
      const mode = await fetchMode().catch(() => null)
      if (
        mode &&
        !mode.snapshotOnSpaceBilling &&
        (scope === 'snapshotOnly' || !mode.imageOnSpaceBilling)
      )
        return
      if (!mountedRef.current) return
      if (Date.now() - startedAt >= VOLUME_REMOVAL_POLL_TIMEOUT_MS) return
      await sleep(VOLUME_REMOVAL_POLL_INTERVAL_MS)
    }
  }

  const run = async () => {
    setSubmitting(true)
    setFailedStep(null)
    try {
      const mode = await fetchMode().catch(() => null)
      if (!mode) {
        setFailedStep({ resource: 'image', code: -1 })
        return
      }

      if (scope !== 'snapshotOnly' && mode.imageOnSpaceBilling) {
        const res: any = await API.switchImageBilling({ op: 2 }, { errMsg: false }).catch(() => ({
          code: -1,
        }))
        if (res?.code !== 0) {
          setFailedStep({
            resource: 'image',
            code: typeof res?.code === 'number' ? res.code : -1,
          })
          return
        }
      }

      if (mode.snapshotOnSpaceBilling) {
        const result = await switchSnapshotWithRetry()
        if (!result.success) {
          setPartial(true)
          setFailedStep({ resource: 'snapshot', code: result.code })
          return
        }
      }

      await waitVolumesRemoved()
      markBillingSwitchCompleted()
      if (!mountedRef.current) return
      message.success(t('space.switchBilling.successMsg', 'Switched to time-based billing'))
      onOk()
    } finally {
      if (mountedRef.current) {
        setSubmitting(false)
      }
    }
  }

  const handleClose = () => {
    if (!submitting) {
      onClose()
    }
  }

  const descKey =
    scope === 'snapshotOnly'
      ? 'descSnapshotOnly'
      : imageOnSpaceBilling && snapshotOnSpaceBilling
        ? 'descBoth'
        : imageOnSpaceBilling
          ? 'descImageOnly'
          : 'descSnapshotOnly'
  const descDefaults: Record<string, string> = {
    descBoth:
      "Image and Snapshot will both move from Space billing to time-based billing, then you can pay for each service's actual usage time",
    descImageOnly:
      'Image will move from Space billing to time-based billing, then you can pay for its actual usage time',
    descSnapshotOnly:
      'Snapshot will move from Space billing to time-based billing, then you can pay for its actual usage time',
  }

  return (
    <Modal className="w-[480px]" open={open} onClose={handleClose}>
      <Modal.Header>
        {partial ? (
          <Trans i18nKey="space.switchBilling.errorTitle">Billing Mode Change Incomplete</Trans>
        ) : (
          <Trans i18nKey="space.switchBilling.modalTitle">Switch to Time-Based Billing</Trans>
        )}
      </Modal.Header>
      <Modal.Body>
        {partial ? (
          <div className="t-sm text-secondary">
            <Trans i18nKey="space.switchBilling.errorDesc">
              We couldn&apos;t complete the billing mode change. Please contact our support team for
              assistance.
            </Trans>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="t-sm text-primary">
              <Trans i18nKey={`space.switchBilling.${descKey}`}>{descDefaults[descKey]}</Trans>
            </div>
            <div className="t-sm text-primary">
              <Trans
                i18nKey="space.switchBilling.permanentNotice"
                components={[<span className="text-error" />]}
              >
                This change is <span className="text-error">permanent and cannot be undone</span>.
                Please confirm to proceed.
              </Trans>
            </div>
            {failedStep && (
              <div className="t-sm text-error">
                <Trans i18nKey="space.switchBilling.imageFailed" values={{ code: failedStep.code }}>
                  Image switch failed (error code: {{ code: failedStep.code }})
                </Trans>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" disabled={submitting} onClick={handleClose}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        {partial ? (
          <Button
            type="secondary"
            onClick={() => {
              window.location.href = 'mailto:support@glows.ai'
            }}
          >
            <Trans i18nKey="space.switchBilling.contactSupportBtn">Contact Support</Trans>
          </Button>
        ) : (
          <Button type="danger" disabled={submitting} onClick={run}>
            <Trans i18nKey="space.switchBilling.confirmBtn">Confirm Switch</Trans>
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
